/*
 * Copyright 2022-2024 benelog GmbH & Co. KG
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const getDefaultState = () => ({
  sourceDestinationType: '',
  sourceCount: 0,
  sources: [],
  source: {},
  destinationCount: 0,
  destinations: [],
  destination: {},
  currentSourceDestinationInfo: null,
  currentSourceDestinationId: null,
  vocabularySyntax: 'URN'
})

export const state = () => getDefaultState()

export const mutations = {
  // Set vocabulary syntax
  setVocabularySyntax(state, vocabularySyntax) {
    state.vocabularySyntax = vocabularySyntax
  },

  // Reset all SourceDestination data
  resetSourceDestinationData(state) {
    Object.assign(state, getDefaultState())
  },

  // Set source/destination type when adding new one
  sourceDestinationTypePopulator(state, sourceDestinationType) {
    state.sourceDestinationType = sourceDestinationType
  },

  // Add or update source/destination
  sourceDestinationAddition(state, sourceDestination) {
    const type = state.sourceDestinationType
    const isNew = state.currentSourceDestinationId == null && state.currentSourceDestinationInfo == null

    if (type === 'source') {
      if (isNew) {
        sourceDestination.ID = state.sourceCount
        state.sources.push(sourceDestination)
        state.sourceCount++
      } else {
        const existing = state.sources.find(obj => obj.ID === state.currentSourceDestinationId)
        if (existing) Object.assign(existing, sourceDestination)
        state.currentSourceDestinationInfo = null
        state.currentSourceDestinationId = null
      }
    } else if (type === 'destination') {
      if (isNew) {
        sourceDestination.ID = state.destinationCount
        state.destinations.push(sourceDestination)
        state.destinationCount++
      } else {
        const existing = state.destinations.find(obj => obj.ID === state.currentSourceDestinationId)
        if (existing) Object.assign(existing, sourceDestination)
        state.currentSourceDestinationInfo = null
        state.currentSourceDestinationId = null
      }
    }
  },

  // Load existing source/destination info for editing
  modifySourceDestination(state, sourceDestinationID) {
    state.currentSourceDestinationId = sourceDestinationID

    if (state.sourceDestinationType === 'source') {
      state.currentSourceDestinationInfo = state.sources.find(obj => obj.ID === sourceDestinationID) || null
    } else if (state.sourceDestinationType === 'destination') {
      state.currentSourceDestinationInfo = state.destinations.find(obj => obj.ID === sourceDestinationID) || null
    }
  },

  // Delete source/destination by ID
  deleteSourceDestination(state, sourceDestinationID) {
    if (state.sourceDestinationType === 'source') {
      const idx = state.sources.findIndex(obj => obj.ID === sourceDestinationID)
      if (idx !== -1) state.sources.splice(idx, 1)
    } else if (state.sourceDestinationType === 'destination') {
      const idx = state.destinations.findIndex(obj => obj.ID === sourceDestinationID)
      if (idx !== -1) state.destinations.splice(idx, 1)
    }
  },

  // Populate raw data (for editing or loading existing design)
  populateRawData(state, payload) {
    state.sources = Array.isArray(payload.sources) ? payload.sources : []
    state.destinations = Array.isArray(payload.destinations) ? payload.destinations : []

    state.sourceCount = state.sources.length > 0 ? state.sources[state.sources.length - 1].ID + 1 : 0
    state.destinationCount = state.destinations.length > 0 ? state.destinations[state.destinations.length - 1].ID + 1 : 0
  },

  // Reset current editing info
  resetCurrentSourceDestination(state) {
    state.currentSourceDestinationInfo = null
    state.currentSourceDestinationId = null
  }
}
